import type { FeatureContext } from './featureContext';
import { initLeaderboardWindow } from '../leaderboardWindow';

/**
 * The art name of the leaderboard bulletin board static authored against the
 * bank's front wall (`static-0x1e5e` at (89,101), ADR-0045). The renderer builds it
 * as an ordinary map static; the installer finds the billboard by this name and makes
 * it clickable - a left-click opens the draggable Top Banksitters window.
 */
export const BOARD_ART = 'static-0x1e5e';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface LeaderboardInstallDeps {
  initLeaderboardWindow?: typeof initLeaderboardWindow;
}

/**
 * The in-world Top Banksitters window (ADR-0045): opened by clicking the
 * leaderboard bulletin board on the front wall, dragged by its title bar, closed
 * with a right-click. It reads the same `/leaderboard` standings the splash panel
 * reads, fetched on open and then re-polled while the window stays open (ADR-0070)
 * so the standings stay live. Pure client UI over a public read. The billboard
 * wiring re-attaches on every renderer (re)build, so a reconnect or an arena
 * scene swap back to town keeps the board clickable.
 */
export function installLeaderboard(ctx: FeatureContext, deps: LeaderboardInstallDeps = {}): void {
  const boardWindow = (deps.initLeaderboardWindow ?? initLeaderboardWindow)(ctx.ui, {
    fetchPage: ctx.fetchLeaderboardPage,
    // The name the player typed on the splash (updated on every connect/retry),
    // so "Jump to your position" asks the server for *this* viewer's rank. The
    // server normalizes it exactly like login, so casing/whitespace can't miss.
    viewerName: () => ctx.viewerName(),
  });
  // The Card Duels tab follows the same server-authoritative `cardsEnabled` launch
  // flag as the rest of the card game (ADR-0290/0300): the Welcome's flag shows/hides
  // it, exactly like the toolbar Cards button in `installCards`.
  ctx.events.on('cardsEnabled', (enabled) => boardWindow.setCardsEnabled(enabled));
  ctx.onRendererBuilt((renderer) => {
    // Pooled statics are configured each time this authored placement enters
    // the viewport.
    // Pool release clears the listener before the sprite is reused.
    renderer.onStaticActivated(BOARD_ART, (sprite) => {
      sprite.eventMode = 'static';
      sprite.cursor = 'inherit';
      sprite.on('click', () => boardWindow.open());
    });
  });
}
